import store from "@/store";
import { errorHandler } from "@/services/errorHandler";
import router from "@/router";
import { Storage } from "@/services/Storage";
import axios from "axios";

export const responses = {
    throwErr(err, title = `Oops`) {
        return Swal.fire({
            title: title,
            icon: `warning`,
            html: `<p>${err}</p>`,
            showCloseButton: true,
            allowOutsideClick: false,
            allowEscapeKey: false
        })
    },

    showSuccess(msg) {
        return window.Toast.fire({
            icon: 'success',
            title: msg
        });
    },

    showInfo(msg) {
        return window.Toast.fire({
            icon: 'info',
            title: msg
        });
    },

    /**
     * Show a red error toast. Used by stock-transfer, deep links,
     * cooking-instruction, and payment flows (callers in
     * SendingItemList.vue, CookingInstruction.vue,
     * CookingInstructionModal.vue, deepLinkService.js, and
     * ViewInvoicesView.vue). Falls back to `console.error` if the
     * global `window.Toast` mixin is not yet injected.
     * @param {string} msg - message to surface
     * @returns {Promise|undefined}
     */
    showError(msg) {
        if (window.Toast && typeof window.Toast.fire === 'function') {
            return window.Toast.fire({
                icon: 'error',
                title: msg
            });
        }
        // Fallback to console so the call site never crashes when the
        // toast mixin is not initialised (e.g. very early in app boot
        // or in unit tests).
        // eslint-disable-next-line no-console
        console.error('[responses.showError]', msg);
    },

    async deviceErr(err, title = `An Error Occurred`) {
        await store.dispatch('fetchResetCount')
        let count = store.getters.getResetCount;

        // Check if remaining resets are zero
        if (count.remainingResets === 0) {
            // Show initial prompt
            const initialResult = await Swal.fire({
                title: 'No Reset Counts Remaining',
                icon: 'warning',
                html: `
                    <p>${err}</p>
                    <br/>
                    <p>You have used all ${count.resets} reset count(s) available.</p>
                    <p><strong>To add an additional PC, please request an upgrade.</strong></p>
                `,
                showCancelButton: true,
                confirmButtonText: 'Request Upgrade',
                cancelButtonText: 'Switch Store',
                allowOutsideClick: false,
                allowEscapeKey: false
            });

            if (initialResult.isConfirmed) {
                // Prompt for contact details
                const { value: contactDetails, isConfirmed } = await Swal.fire({
                    title: 'Contact Details',
                    html: `
                        <div style="text-align: left;">
                            <div style="margin-bottom: 12px;">
                                <label style="display: block; margin-bottom: 4px; font-weight: 500;">Contact Name</label>
                                <input id="swal-contact-name" class="swal2-input" placeholder="Your name" style="margin: 0; width: 100%;">
                            </div>
                            <div style="margin-bottom: 12px;">
                                <label style="display: block; margin-bottom: 4px; font-weight: 500;">Position</label>
                                <input id="swal-contact-position" class="swal2-input" placeholder="e.g. Manager, Owner" style="margin: 0; width: 100%;">
                            </div>
                            <div>
                                <label style="display: block; margin-bottom: 4px; font-weight: 500;">Phone Number</label>
                                <input id="swal-contact-phone" class="swal2-input" placeholder="Phone number" style="margin: 0; width: 100%;">
                            </div>
                        </div>
                    `,
                    focusConfirm: false,
                    showCancelButton: true,
                    confirmButtonText: 'Submit Request',
                    cancelButtonText: 'Cancel',
                    confirmButtonColor: '#339CFF',
                    preConfirm: () => {
                        const name = document.getElementById('swal-contact-name').value.trim();
                        const position = document.getElementById('swal-contact-position').value.trim();
                        const phone = document.getElementById('swal-contact-phone').value.trim();

                        if (!name || !position || !phone) {
                            Swal.showValidationMessage('Please fill in all fields');
                            return false;
                        }

                        return { name, position, phone };
                    }
                });

                if (isConfirmed && contactDetails) {
                    try {
                        const defaultStore = await Storage.getDefaultStore();
                        const encodedStoreDB = window.btoa(defaultStore.StoreDB);
                        const url = ticketApi + 'ticket/createSimplifiedTicket';

                        const ticketData = {
                            type: 'billing',
                            title: 'Add Device Request - Additional PC License',
                            platform: 'Lytning Retail POS',
                            description: `Add Device Request\n\nStore Name: ${defaultStore.StoreName}\nTotal Resets: ${count.resets}\nResets Used: ${count.resets}\n\nThe customer has exhausted all device reset counts and is requesting to add an additional PC to their license.`,
                            allStores: false,
                            contactName: contactDetails.name,
                            contactPosition: contactDetails.position,
                            customerContact: contactDetails.phone,
                        };

                        await axios.post(url, ticketData, {
                            headers: {
                                DB: encodedStoreDB,
                            },
                        });

                        await Swal.fire({
                            icon: 'success',
                            title: 'Request Submitted',
                            text: 'Your device upgrade request has been submitted successfully. Our team will contact you soon.',
                            confirmButtonColor: '#339CFF',
                        });
                    } catch (error) {
                        console.error('[deviceErr] Failed to create device upgrade ticket:', error);
                        await Swal.fire({
                            icon: 'error',
                            title: 'Request Failed',
                            text: 'Failed to submit upgrade request. Please try again or contact support.',
                            confirmButtonColor: '#339CFF',
                        });
                    }
                }
            } else if (initialResult.dismiss === Swal.DismissReason.cancel) {
                // Wipe bound store then land on store picker (keep franchise)
                await store.dispatch('switchStore');
            }

            return;
        }

        // Continue with normal reset flow if resets are available
        return Swal.fire({
            title: `Registration Error`,
            icon: 'warning',
            html: `
            <span>${err}</span><br/>
            <br/>
            <span>Number Of Reset Count(s): ${count.resets}</span><br/>
            <span>Remaining Resets Count(s): ${count.remainingResets}</span>
        `,
            stopKeydownPropagation: true,
            confirmButtonText: 'Reset Device',
            allowOutsideClick: false,
            allowEscapeKey: false

        }).then((result) => {
            if (result.isConfirmed) {
                return Swal.fire({
                    title: "Enter Your Name",
                    input: "text",
                    confirmButtonText: 'Reset',
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showLoaderOnConfirm: true,
                    stopKeydownPropagation: true,
                    preConfirm: (name) => {
                        if (!name) {
                            Swal.showValidationMessage('Name is required');
                            return false;
                        }
                        return store.dispatch('ResetDevices', name)
                            .catch(async (err) => {
                                errorHandler.tomcatError(err);
                                // Force navigation to store selection on reset failure
                                await Swal.fire({
                                    title: 'Device Reset Failed',
                                    icon: 'error',
                                    text: 'Device reset failed. Please switch to another store.',
                                    confirmButtonText: 'Go to Store Selection',
                                    allowOutsideClick: false,
                                    allowEscapeKey: false
                                });
                                await store.dispatch('switchStore');
                                return false;
                            });
                    }
                }).then((finalResult) => {
                    if (finalResult.isConfirmed) {
                        return Swal.fire({
                            title: "Devices Reset",
                            icon: "success",
                            text: "Your license have been reset successfully!. Please login again",
                            allowOutsideClick: false,
                            allowEscapeKey: false,
                            stopKeydownPropagation: true
                        });
                    }
                });
            }
        });
    },

    showWarning(msg, timer = 1500) {
        return window.Toast.fire({
            icon: 'warning',
            title: msg,
            timer: timer
        });
    },
}
